import React, { Fragment, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import StandardModal, { Actions, ModalContent } from 'components/shared/StandardModal';
import { useProfile } from 'context/profile';
import { simpleDateAtTime } from 'lib/date';
import { PERMISSIONS } from 'models/flexible/Profile';
import {
  adminImportsQueryKeys,
  adminShowImport,
  adminUpdateImport,
  ErrorType,
  IMPORT_PROCESS_STEPS,
  ImportAction,
  ImportStep,
  ImportStepStatus,
  NewAttrValueCount,
  StepResultsErrorMessage,
} from 'services/api/admin/imports';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold, Label } from 'styles/typography';
import CompletedStatusIcon from 'svg/CompletedStatusIcon';
import ErrorStatusIcon from 'svg/ErrorStatusIcon';
import PendingStatusIcon from 'svg/PendingStatusIcon';

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const STATUS_ICON_MAP: Record<string, React.FC<any>> = {
  [ImportStepStatus.COMPLETED]: CompletedStatusIcon,
  [ImportStepStatus.ERROR]: ErrorStatusIcon,
  [ImportStepStatus.RUNNING]: PendingStatusIcon,
  [ImportStepStatus.PENDING]: PendingStatusIcon,
};

const STATUS_COLORS: Record<string, string> = {
  pending: colors.black25,
  running: colors.primaryBlue,
  paused: colors.accentYellow,
  completed: colors.accentGreen,
  errored: colors.accentRed,
  canceled: colors.black50,
};

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function humanize(s: string) {
  return s.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
}

// ---------------------------------------------------------------------------
// ImportStatusDetails
// ---------------------------------------------------------------------------

export default function ImportStatusDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useProfile();
  const canEdit = profile.has(PERMISSIONS.adminImportedPatientEdit);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const { data: importObj, refetch } = useQuery({
    queryKey: adminImportsQueryKeys.show(id!),
    queryFn: ({ signal }) => adminShowImport(id!, signal, ['child', 'client', 'steps'] as const),
    enabled: !!id,
  });

  const sortedSteps = useMemo(() => {
    if (!importObj?.steps) return [];
    return [...importObj.steps].sort((a, b) => a.order - b.order);
  }, [importObj?.steps]);

  const goToImports = () => navigate('/admin/import-history');

  const handleAction = async (action: ImportAction) => {
    await adminUpdateImport(importObj!.id, { invokeAction: action });
    if (action === ImportAction.CANCEL || action === ImportAction.RESTART) {
      goToImports();
    } else {
      refetch();
    }
  };

  if (!importObj) return null;

  return (
    <PageContainer>
      <HeaderSection>
        <Header>
          <Title>Import Status Details</Title>
          {canEdit && (
            <ButtonContainer>
              {importObj.cancellable && (
                <Button variant='outline-danger' onClick={() => setShowCancelModal(true)}>
                  Cancel Import
                </Button>
              )}
              {importObj.runnable && <Button onClick={() => handleAction(ImportAction.RUN)}>Resume Import</Button>}
              {importObj.restartable && (
                <Button onClick={() => handleAction(ImportAction.RESTART)}>Restart Import</Button>
              )}
            </ButtonContainer>
          )}
        </Header>

        <StatusInfoBar>
          <Row>
            <StyledBodySmallBold>Client:&nbsp;</StyledBodySmallBold>
            <BodySmallBold>{importObj.client?.name ?? '—'}</BodySmallBold>
          </Row>
          <StatusRow>
            <StyledBodySmallBold>Status:&nbsp;</StyledBodySmallBold>
            <StatusDot $color={STATUS_COLORS[importObj.status] ?? colors.black25} />
            <span style={{ textTransform: 'capitalize' }}>{importObj.status}</span>
          </StatusRow>
          <Row>
            <StyledBodySmallBold>Import Created On:&nbsp;</StyledBodySmallBold>
            <BodySmallBold>{importObj.createdAt ? simpleDateAtTime(importObj.createdAt) : '—'}</BodySmallBold>
          </Row>
        </StatusInfoBar>
      </HeaderSection>

      <StepsSection>
        {sortedSteps.map((step, i) => (
          <StatusDetailsCard key={i} step={step} />
        ))}
      </StepsSection>

      {showCancelModal && (
        <StandardModal title='Cancel Import' onCancel={() => setShowCancelModal(false)}>
          <ModalContent>
            <BodySmall>Are you sure you want to cancel this import?</BodySmall>
          </ModalContent>
          <Actions>
            <Button variant='ghost' onClick={() => setShowCancelModal(false)}>
              Cancel
            </Button>
            <Button
              variant='danger'
              onClick={async () => {
                await handleAction(ImportAction.CANCEL);
                setShowCancelModal(false);
              }}>
              Confirm
            </Button>
          </Actions>
        </StandardModal>
      )}
    </PageContainer>
  );
}

// ---------------------------------------------------------------------------
// StatusDetailsCard
// ---------------------------------------------------------------------------

function StatusDetailsCard({ step }: { step: ImportStep }) {
  const { apiType, status } = step;
  const Icon = STATUS_ICON_MAP[status] || PendingStatusIcon;
  const formattedType = IMPORT_PROCESS_STEPS[apiType] || apiType;

  return (
    <CardContainer>
      <CardHeader>
        <IconContainer>
          <Icon />
        </IconContainer>
        <div>
          <BodySmallBold>{formattedType}</BodySmallBold>
          <StyledLabel>{capitalize(status)}</StyledLabel>
        </div>
      </CardHeader>
      {status && step.hasResults && <StatusStepResults step={step} />}
    </CardContainer>
  );
}

// ---------------------------------------------------------------------------
// StatusStepResults
// ---------------------------------------------------------------------------

function StatusStepResults({ step }: { step: ImportStep }) {
  const { results } = step;

  const renderListItem = (value: number | string[] | NewAttrValueCount[]) => {
    if (Array.isArray(value)) {
      return (
        <List>
          {value.map((item, i) => {
            const formatted = typeof item === 'object' ? `${item.key}: ${item.value}` : item;
            return (
              <li key={i}>
                <InlineBodySmall>{formatted}</InlineBodySmall>
              </li>
            );
          })}
        </List>
      );
    }
    return <InlineBodySmall>{value}</InlineBodySmall>;
  };

  const renderRowNumbers = (rowNumbers: number[]) => {
    return (
      <Fragment>
        <br />
        Row Numbers: {rowNumbers.join(', ')}
      </Fragment>
    );
  };

  const renderErrorListItems = (errors: StepResultsErrorMessage[]) => {
    return errors.map((error, i) => {
      const { message, rowNumbers, totalCount, link, type } = error;
      return (
        <li key={i}>
          <InlineBodySmall>
            {message}
            {!!link && (
              <StyledLink href={link} target='_blank' rel='noopener noreferrer'>
                {' '}
                View Details
              </StyledLink>
            )}
            <br />
            {!!totalCount && type !== ErrorType.WARNING && `Total Occurrences: ${totalCount}`}
            {!!rowNumbers.length && renderRowNumbers(rowNumbers)}
          </InlineBodySmall>
        </li>
      );
    });
  };

  const renderResults = () => {
    return Object.entries(results)
      .filter(([k]) => k !== 'errorMsgs')
      .map(([key, value], i) => {
        if (Array.isArray(value) && !value.length) return null;
        return (
          <li key={i}>
            {`${capitalize(humanize(key))}: `}
            {renderListItem(value as any)}
          </li>
        );
      });
  };

  const renderErrorMsgs = (label: string, items: StepResultsErrorMessage[]) => {
    const pluralLabel = items.length > 1 ? `${label}s` : label;
    return (
      <List>
        <li>
          {`${pluralLabel}: (${items.length})`}
          <List>{renderErrorListItems(items)}</List>
        </li>
      </List>
    );
  };

  return (
    <ResultsContainer>
      <List>{renderResults()}</List>
      {Boolean(step.warnings.length) && renderErrorMsgs('Warning', step.warnings)}
      {Boolean(step.errors.length) && renderErrorMsgs('Error', step.errors)}
    </ResultsContainer>
  );
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const PageContainer = styled.div`
  flex: 1;
  height: 100%;
  overflow: auto;
`;

const HeaderSection = styled.div`
  background: white;
  padding: 24px 24px 0;
  border-bottom: 1px solid var(--black-10);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const StatusInfoBar = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 24px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  margin-right: 24px;

  &:last-child {
    margin-right: 0;
  }
`;

const StatusRow = styled(Row)`
  display: flex;
  align-items: center;
`;

const StyledBodySmallBold = styled(BodySmallBold)`
  color: ${colors.black50};
`;

const StatusDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  margin: 0 8px 0 4px;
`;

const StepsSection = styled.div`
  padding: 24px;
`;

const CardContainer = styled.div`
  background: white;
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  width: 500px;
  margin-bottom: 8px;
`;

const CardHeader = styled.div`
  padding: 24px;
  display: flex;
`;

const IconContainer = styled.div`
  margin-right: 16px;
`;

const StyledLabel = styled(Label)`
  width: 100%;
  margin-top: 8px;
  color: ${colors.black50};
`;

const ResultsContainer = styled.div`
  border-top: 1px solid ${colors.black05};
  padding: 0px 16px 4px 0px;
  max-width: 500px;
`;

const List = styled.ul`
  line-height: 2;
  padding-left: 40px;
  margin: 14px 0;
  font-size: 14px;
`;

const InlineBodySmall = styled(BodySmall)`
  display: inline;
`;

const StyledLink = styled.a`
  color: ${colors.primaryBlue};
  &:hover {
    cursor: pointer;
  }
`;
